import { Sparkles, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';
import { Badge } from '../ui/Badge';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { CompletenessGap, GapCategory, GapStatus } from '../../types/completenessGap';

interface GapCardProps {
  gap: CompletenessGap;
  onAnalyze: (gapId: string) => void;
  onResolve: (gapId: string) => void;
  onDismiss: (gapId: string) => void;
  onReopen: (gapId: string) => void;
}

export default function GapCard({ gap, onAnalyze, onResolve, onDismiss, onReopen }: GapCardProps) {
  const getCategoryInfo = (category: GapCategory) => {
    switch (category) {
      case 'why_not_100':
        return {
          label: 'Why Not 100%',
          color: 'warning',
          icon: '📊',
        };
      case 'missing_data':
        return {
          label: 'Missing Data',
          color: 'error',
          icon: '🔍',
        };
      case 'clarification':
        return {
          label: 'Clarification',
          color: 'neutral',
          icon: '❓',
        };
    }
  };

  const getStatusInfo = (status: GapStatus) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Pending',
          color: 'warning',
          icon: <AlertCircle className="w-4 h-4" />,
        };
      case 'in_progress':
        return {
          label: 'In Progress',
          color: 'primary',
          icon: <Loader className="w-4 h-4" />,
        };
      case 'resolved':
        return {
          label: 'Resolved',
          color: 'success',
          icon: <CheckCircle className="w-4 h-4" />,
        };
      case 'dismissed':
        return {
          label: 'Dismissed',
          color: 'neutral',
          icon: <XCircle className="w-4 h-4" />,
        };
    }
  };

  const categoryInfo = getCategoryInfo(gap.category);
  const statusInfo = getStatusInfo(gap.status);
  const isActionable = gap.status === 'pending' || gap.status === 'in_progress';
  const isDismissedOrResolved = gap.status === 'dismissed' || gap.status === 'resolved';

  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="space-y-3">
        {/* Header with badges */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={categoryInfo.color as any} size="sm">
              {categoryInfo.icon} {categoryInfo.label}
            </Badge>
            <Badge variant={statusInfo.color as any} size="sm">
              <span className="flex items-center gap-1">
                {statusInfo.icon}
                {statusInfo.label}
              </span>
            </Badge>
          </div>
          <div className="text-right">
            <div className="text-xs text-neutral-500">Impact</div>
            <div className="text-lg font-bold text-primary-600">
              {gap.impactScore.toFixed(1)}
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <p className="text-sm text-neutral-700 leading-relaxed">
            {gap.description}
          </p>
        </div>

        {/* AI Actions */}
        {gap.aiActions && gap.aiActions.length > 0 && (
          <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-900">
                Amplinth-Suggested Actions
              </span>
            </div>
            <ul className="space-y-1">
              {gap.aiActions.map((action, index) => (
                <li key={index} className="text-xs text-blue-800 pl-4">
                  • {action}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Dismissal Reason */}
        {gap.status === 'dismissed' && gap.dismissalReason && (
          <div className="bg-neutral-50 p-3 rounded-md border border-neutral-200">
            <div className="text-xs font-medium text-neutral-700 mb-1">
              Dismissal Reason:
            </div>
            <p className="text-xs text-neutral-600 italic">
              {gap.dismissalReason}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
          {isActionable && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => onResolve(gap.gapId)}
              >
                <Sparkles className="w-4 h-4" />
                Resolve with Business Transformation Studio
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAnalyze(gap.gapId)}
              >
                Analyze
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDismiss(gap.gapId)}
              >
                Dismiss
              </Button>
            </>
          )}

          {isDismissedOrResolved && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onReopen(gap.gapId)}
            >
              Reopen
            </Button>
          )}
        </div>

        {/* Metadata */}
        <div className="text-xs text-neutral-500">
          Created {new Date(gap.createdAt).toLocaleDateString()}
          {gap.resolvedAt && (
            <> • Resolved {new Date(gap.resolvedAt).toLocaleDateString()}</>
          )}
          {gap.dismissedAt && (
            <> • Dismissed {new Date(gap.dismissedAt).toLocaleDateString()}</>
          )}
        </div>
      </div>
    </Card>
  );
}
